import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Image,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  useDisclosure,
  useColorModeValue,
  Icon,
  Button,
  IconButton,
  Avatar,
  Divider,
  Badge,
} from '@chakra-ui/react';
import { 
  MdDashboard, 
  MdMenu, 
  MdExitToApp,
  MdGroup,
  MdSearch,
  MdAssignment,
  MdDescription,
  MdSupervisedUserCircle
} from 'react-icons/md';
import { FaChevronRight, FaBalanceScale } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import './DiretoriaJuridicoSidebar.css';

const DiretoriaJuridicoSidebar = () => {
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  useEffect(() => {
    const userDataString = localStorage.getItem('userInfo');
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      setUserInfo(userData);
    }
  }, []);

  const bgSidebar = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'white');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');
  const activeBg = 'blue.500';
  const activeTextColor = 'white';
  const scrollbarThumbColor = useColorModeValue('gray.300', 'gray.600');

  const logoSrc = useColorModeValue(
    '/Assets/Logos/logo_mini.png',
    '/Assets/Logos/Logo-night.png'
  );

  const logout = () => {
    localStorage.removeItem('userInfo');
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const SidebarItem = ({ icon, label, to, badge }) => {
    const isMobile = window.innerWidth < 992;
    
    return (
      <Flex
        align="center"
        px="4"
        py={{ base: 3, lg: 2.5 }}
        mx={{ base: 0, lg: 2 }}
        borderRadius={{ base: "md", lg: "lg" }}
        role="group"
        cursor="pointer"
        bg={isActive(to) ? activeBg : 'transparent'}
        color={isActive(to) ? activeTextColor : textColor}
        _hover={{
          bg: isActive(to) ? activeBg : hoverBg,
          color: isActive(to) ? activeTextColor : textColor,
        }}
        transition="all 0.2s"
        onClick={() => {
          to && navigate(to);
          if (isMobile && window.onCloseMobileSidebar) {
            window.onCloseMobileSidebar();
          }
        }}
      >
        <Icon 
          as={icon} 
          fontSize={{ base: "24px", lg: "20px" }}
          mr="3"
          color={isActive(to) ? activeTextColor : 'gray.500'}
        />
        <Text fontSize={{ base: "md", lg: "sm" }} fontWeight={isActive(to) ? "semibold" : "medium"}>
          {label}
        </Text>
        {badge && (
          <Badge ml="2" colorScheme="red" fontSize="xs">
            {badge}
          </Badge>
        )}
        {isActive(to) && (
          <Icon 
            as={FaChevronRight} 
            fontSize={{ base: "16px", lg: "14px" }}
            ml="auto" 
            color={activeTextColor}
          />
        )}
      </Flex>
    );
  };

  const SidebarContent = () => (
    <Box 
      bg={bgSidebar} 
      w={{ base: "100%", lg: "250px" }}
      h="100vh" 
      py="5" 
      borderRight={{ base: "none", lg: "1px" }}
      borderColor={borderColor}
      position={{ base: "relative", lg: "fixed" }}
      left="0"
      top="0"
      overflowY="auto"
      css={{
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          width: '6px',
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: scrollbarThumbColor,
          borderRadius: '24px',
        },
      }}
      shadow={{ base: "none", lg: "lg" }}
      transition="all 0.2s"
      zIndex="1000"
    >
      <Flex 
        direction="column" 
        h="full" 
        justify="space-between"
      >
        <Box>
          <Flex 
            mb="6" 
            px="5" 
            alignItems="center" 
            justifyContent="space-between"
          >
            <Box maxW="70%">
              <Image 
                src={logoSrc}
                alt="Logo"
                objectFit="contain"
                h={{ base: "40px", lg: "35px" }}
                cursor="pointer"
                onClick={() => navigate('/diretoria-juridico/dashboard')}
              />
            </Box>
            
            {/* Mobile close button */}
            <IconButton
              display={{ base: 'flex', lg: 'none' }}
              onClick={onClose}
              variant="ghost"
              aria-label="close menu"
              icon={<Icon as={MdMenu} fontSize="24px" />}
            />
          </Flex>
          
          {/* User Info */}
          <HStack 
            px="5" 
            py="3" 
            mb="4"
            borderRadius="lg"
            bg={useColorModeValue('gray.50', 'gray.700')}
            mx="3"
          >
            <Avatar 
              size="sm" 
              name={userInfo?.name} 
              bg="purple.500"
              color="white"
            />
            <Box flex="1">
              <Text 
                fontSize="sm" 
                fontWeight="medium"
                color={textColor}
                noOfLines={1}
              >
                {userInfo?.name || 'Diretor Jurídico'}
              </Text>
              <Text 
                fontSize="xs" 
                color="gray.500"
                noOfLines={1}
              >
                Diretoria Jurídica
              </Text>
            </Box>
          </HStack>
          
          <Divider mb="4" />
          
          <Text 
            px="5" 
            my="3" 
            color="gray.500" 
            fontSize="xs" 
            fontWeight="medium"
            letterSpacing="wider"
            textTransform="uppercase"
          >
            Menu Principal
          </Text>

          <VStack align="stretch" spacing={{ base: 2, lg: 1 }}>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdDashboard} 
                label="Dashboard" 
                to="/diretoria-juridico/dashboard" 
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdSupervisedUserCircle} 
                label="Gestores" 
                to="/diretoria-juridico/gestores" 
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdGroup} 
                label="Equipe Completa" 
                to="/diretoria-juridico/equipe-completa" 
              />
            </Box>
          </VStack>
          
          <Divider my="4" />
          
          <Text 
            px="5" 
            my="3" 
            color="gray.500" 
            fontSize="xs" 
            fontWeight="medium"
            letterSpacing="wider"
            textTransform="uppercase"
          >
            Operacional
          </Text>
          
          <VStack align="stretch" spacing={{ base: 2, lg: 1 }}>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdSearch} 
                label="Precatórios Disponíveis" 
                to="/diretoria-juridico/precatorios-disponiveis" 
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdAssignment} 
                label="Meus Precatórios" 
                to="/diretoria-juridico/meus-precatorios" 
              />
            </Box>
          </VStack>
          
          <Divider my="4" />
          
          <Text 
            px="5" 
            my="3" 
            color="gray.500" 
            fontSize="xs" 
            fontWeight="medium"
            letterSpacing="wider"
            textTransform="uppercase"
          >
            Relatórios
          </Text>
          
          <VStack align="stretch" spacing={{ base: 2, lg: 1 }}>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdDescription} 
                label="Relatórios do Departamento" 
                to="/diretoria-juridico/relatorios" 
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={FaBalanceScale} 
                label="Análises Jurídicas" 
                to="/diretoria-juridico/analises" 
              />
            </Box>
          </VStack>
        </Box>
        
        <Box>
          <Divider mb="4" />
          <Box px={{ base: 4, lg: 2 }}>
            <Button
              w="full"
              variant="ghost"
              colorScheme="red"
              leftIcon={<MdExitToApp />}
              onClick={logout}
              justifyContent="flex-start"
              px="4"
              fontWeight="medium"
              fontSize={{ base: "md", lg: "sm" }}
              _hover={{
                bg: 'red.50',
                color: 'red.600',
              }}
            >
              Sair
            </Button>
          </Box>
        </Box>
      </Flex>
    </Box>
  );

  // Set the close function globally for mobile
  useEffect(() => {
    if (window) {
      window.onCloseMobileSidebar = onClose;
    }
    return () => {
      if (window) {
        delete window.onCloseMobileSidebar;
      }
    };
  }, [onClose]);

  return (
    <>
      {/* Mobile Menu Button */}
      <IconButton
        display={{ base: 'flex', lg: 'none' }}
        onClick={onOpen}
        variant="outline"
        aria-label="open menu"
        icon={<MdMenu />}
        position="fixed"
        top="4"
        left="4"
        zIndex="999"
        bg={bgSidebar}
        borderColor={borderColor}
        _hover={{
          bg: hoverBg,
        }}
      />

      {/* Mobile Drawer */}
      <Drawer
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        size="xs"
      >
        <DrawerOverlay />
        <DrawerContent maxW="250px">
          <DrawerBody p="0">
            <SidebarContent />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Desktop Sidebar */}
      <Box display={{ base: 'none', lg: 'block' }}>
        <SidebarContent />
      </Box>
    </>
  );
};

export default DiretoriaJuridicoSidebar;